import React, { useCallback, useRef, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { Link } from 'react-router-dom';

export const PROCESSING_DISCLAIMER_POINTS = [
  'DocxsAi uses artificial intelligence to analyze, generate, or revamp your documents. Outputs are provided for informational purposes only and do not constitute legal, immigration, career, financial, or other professional advice.',
  'You confirm that you own the document or have permission to upload and process it, and that it does not contain unlawful or confidential third-party material you are not authorized to share.',
  'Your document content and related inputs may be transmitted to third-party AI service providers for processing, as described in our Privacy Policy.',
  'AI-generated results may be incomplete or inaccurate. You are responsible for reviewing, verifying, and editing all outputs before use, submission, or reliance.',
  'By continuing, you acknowledge our Terms of Service and Privacy Policy.',
];

export function useProcessingDisclaimer() {
  const [open, setOpen] = useState(false);
  const pendingActionRef = useRef(null);

  const requestProcessing = useCallback((action) => {
    pendingActionRef.current = action;
    setOpen(true);
  }, []);

  const confirmProcessing = useCallback(() => {
    setOpen(false);
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    if (typeof action === 'function') {
      action();
    }
  }, []);

  const dismissDisclaimer = useCallback(() => {
    setOpen(false);
    pendingActionRef.current = null;
  }, []);

  return { open, requestProcessing, confirmProcessing, dismissDisclaimer };
}

const ProcessingDisclaimerModal = ({
  open,
  onConfirm,
  onClose,
  actionLabel = 'Continue',
}) => {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="elevated-card rounded-2xl shadow-2xl w-full max-w-lg md:max-w-2xl lg:max-w-3xl relative overflow-hidden animate-scaleIn"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="processing-disclaimer-title"
      >
        <button
          type="button"
          className="absolute top-4 right-4 text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition"
          onClick={onClose}
          aria-label="Close disclaimer"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="px-6 sm:px-8 pt-8 pb-4 flex flex-col items-center text-center border-b border-[var(--color-border)]">
          <div className="w-12 h-12 rounded-full bg-amber-500/15 flex items-center justify-center mb-3">
            <AlertTriangle className="w-6 h-6 text-amber-400" />
          </div>
          <h2 id="processing-disclaimer-title" className="text-xl sm:text-2xl font-bold text-[var(--color-text)]">
            Before We Process Your Document
          </h2>
          {actionLabel ? (
            <p className="text-sm text-[var(--color-text-muted)] mt-2">
              Please review this notice before continuing with <span className="text-[var(--color-text-secondary)]">{actionLabel}</span>.
            </p>
          ) : null}
        </div>

        <div className="px-6 sm:px-8 py-5 max-h-[50vh] overflow-y-auto">
          <ul className="space-y-3 text-sm sm:text-base text-[var(--color-text-secondary)] list-disc pl-5">
            {PROCESSING_DISCLAIMER_POINTS.map((point) => (
              <li key={point} className="leading-relaxed">
                {point.includes('Terms of Service') ? (
                  <>
                    By continuing, you acknowledge our{' '}
                    <Link to="/terms" className="text-blue-400 hover:text-blue-300 underline" onClick={onClose}>
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy" className="text-blue-400 hover:text-blue-300 underline" onClick={onClose}>
                      Privacy Policy
                    </Link>
                    .
                  </>
                ) : (
                  point
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className="px-6 sm:px-8 pb-8">
          <button
            type="button"
            onClick={onConfirm}
            className="brand-gradient-btn w-full px-6 py-3 rounded-lg font-semibold"
          >
            Okay, Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProcessingDisclaimerModal;
